import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function RegisterPage() {
  const { setUser } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success("Willkommen bei ALLES GEREGELT.");
      navigate("/onboarding");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Registrierung fehlgeschlagen"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex bg-ag-paper ag-paper-grain">
      <div className="hidden lg:flex w-1/2 ag-sidebar text-slate-100 flex-col justify-between p-14 relative overflow-hidden">
        <div className="absolute -bottom-20 -right-20 h-96 w-96 rounded-full bg-emerald-400/10 blur-3xl" />
        <div className="relative">
          <Logo variant="full" tone="dark" className="ag-logo-enter h-28 w-auto -ml-3" />
        </div>
        <div className="relative max-w-md">
          <p className="font-serif text-3xl leading-snug text-white/95">
            Schön, dass Sie da sind.
          </p>
          <p className="text-sm text-slate-400 mt-6">
            Schon 10 Minuten reichen, um heute etwas Wichtiges zu regeln. Wir begleiten Sie Schritt für Schritt.
          </p>
        </div>
        <div className="relative text-xs text-slate-500">© ALLES GEREGELT</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center text-center mb-10 lg:hidden">
            <Logo variant="full" tone="light" className="ag-logo-enter h-20 w-auto" />
            <div className="ag-claim-enter mt-4 text-sm text-slate-500 italic">
              Ein gutes Gefühl. Weil alles geregelt ist.
            </div>
          </div>
          <h1 className="font-serif text-4xl text-ag-ink mb-2">{t.auth.welcomeNew}</h1>
          <p className="text-sm text-slate-500 mb-8">{t.auth.register}</p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.auth.firstName}</Label>
                <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="register-firstname" className="mt-1.5 h-11 bg-white" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.auth.lastName}</Label>
                <Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="register-lastname" className="mt-1.5 h-11 bg-white" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t.auth.email}</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" className="mt-1.5 h-11 bg-white" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t.auth.password}</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" className="mt-1.5 h-11 bg-white" />
            </div>
            <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full h-11 bg-ag-ink hover:bg-slate-800 text-white rounded-xl">
              {t.auth.submitRegister} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5 text-xs text-slate-400">
            <div className="h-px flex-1 bg-slate-200" />
            <span>{t.auth.orDivider}</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <Button type="button" variant="outline" onClick={handleGoogle} data-testid="register-google" className="w-full h-11 border-slate-200 bg-white hover:bg-slate-50 rounded-xl">
            {t.auth.google}
          </Button>

          <div className="mt-6 text-sm text-slate-500 text-center">
            {t.auth.hasAccount}{" "}
            <Link to="/login" data-testid="link-login" className="text-ag-ink underline-offset-4 hover:underline font-medium">{t.auth.login}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
